import React, { Dispatch, SetStateAction } from 'react';

import { SelectInput } from '@amzn/imdb-shared-meridian-components/components/InputForm/SelectInput/SelectInput';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import ControlGroup from '@amzn/meridian/control-group';
import Heading from '@amzn/meridian/heading';
import Input from '@amzn/meridian/input';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import RadioButton from '@amzn/meridian/radio-button';
import Row from '@amzn/meridian/row';
import Textarea from '@amzn/meridian/textarea';

import { ListDetails, ListFlag, ListFlagReasonCode, ListState } from '../../../listoramaAdmin-api/generated-src';
import { SelectedLists } from '../../features/UserDetailsSection/components/UserListsDataTable/UserListsDataTable';
import { ListActionButtonConfig, ListButtonId } from '../ListActionButtons/ListActionButtons';

export const FLAG_NOTE_HINT = 'Optional note. Reference tickets by ID, do not paste customer content.';

export interface ListModalData {
    isOpen: boolean;
    buttonProps?: ListActionButtonConfig<ListButtonId>;
}

export interface ListEditData {
    state?: ListState;
    flag?: ListFlag;
    // Structured context for flag changes, persisted on the flag's audit event and shown in the
    // listDetails flag history.
    reasonCode?: ListFlagReasonCode;
    note?: string;
    ticketId?: string;
}

/**
 * The admin API's documented bounds (FlagNote and TicketId in listorama_admin_api.yml).
 */
export const FLAG_NOTE_MAX_LENGTH = 4000;
export const FLAG_TICKET_ID_MAX_LENGTH = 128;

interface FlagContextErrors {
    note?: string;
    ticketId?: string;
}

const formatCount = (count: number) => count.toLocaleString('en-US');

const describeTooLong = (field: string, value: string | undefined, maxLength: number): string | undefined =>
    value !== undefined && value.length > maxLength
        ? `${field} is ${formatCount(value.length)} characters; the limit is ${formatCount(maxLength)}.`
        : undefined;

const getFlagContextErrors = (listEditData: ListEditData): FlagContextErrors => ({
    note: describeTooLong('Note', listEditData.note, FLAG_NOTE_MAX_LENGTH),
    ticketId: describeTooLong('Ticket ID', listEditData.ticketId, FLAG_TICKET_ID_MAX_LENGTH)
});

// Reason context exists only on the flag modals, where an over-long value would fail the whole flag change.
const hasFlagContextErrors = (buttonId: ListButtonId, listEditData: ListEditData): boolean => {
    if (buttonId !== 'add_list_flag' && buttonId !== 'remove_list_flag') {
        return false;
    }
    const errors = getFlagContextErrors(listEditData);
    return errors.note !== undefined || errors.ticketId !== undefined;
};

interface ListActionModalProps {
    modalData: ListModalData;
    setModalData: Dispatch<SetStateAction<ListModalData>>;
    listEditData: ListEditData;
    setListEditData: Dispatch<SetStateAction<ListEditData>>;
    list?: ListDetails;
    selectedLists?: SelectedLists;
}

export const ListActionModal: React.FC<ListActionModalProps> = (props: ListActionModalProps) => {
    const { list, selectedLists, modalData, setModalData, listEditData, setListEditData } = props;

    const onModalClose = () => {
        setModalData({ isOpen: false });
        setListEditData((prevState) => ({
            ...prevState,
            flag: undefined,
            reasonCode: undefined,
            note: undefined,
            ticketId: undefined
        }));
    };

    return (
        <Modal width={400} onClose={onModalClose} title={modalData.buttonProps?.text} open={modalData.isOpen}>
            {modalData.buttonProps ? (
                createModalContent(list, selectedLists, modalData.buttonProps, listEditData, setListEditData)
            ) : (
                <></>
            )}
        </Modal>
    );
};

interface FlagSelectInputProps {
    list: ListDetails | undefined;
    buttonId: ListButtonId;
    listEditData: ListEditData;
    setListEditData: Dispatch<SetStateAction<ListEditData>>;
}

const FlagSelectInput = (props: FlagSelectInputProps) => {
    const { list, buttonId, listEditData, setListEditData } = props;

    const isFlagInDropdown = (flag: ListFlag) => {
        switch (buttonId) {
            case 'add_list_flag':
                return list && list.flags.indexOf(flag) < 0;
            case 'remove_list_flag':
                return list && list.flags.indexOf(flag) >= 0;
        }
    };

    const options = Object.keys(ListFlag)
        .filter((key) => (list ? isFlagInDropdown(ListFlag[key]) : true))
        .map((key) => ({ key, value: ListFlag[key], label: ListFlag[key] }));
    return (
        <SelectInput
            id={buttonId}
            placeholder='Select flag'
            value={listEditData.flag!}
            onChange={(flag) => setListEditData((prevState) => ({ ...prevState, flag }))}
            options={options}
        />
    );
};

interface FlagReasonContextInputsProps {
    listEditData: ListEditData;
    setListEditData: Dispatch<SetStateAction<ListEditData>>;
}

const REASON_CODE_OPTIONS = Object.keys(ListFlagReasonCode).map((key) => ({
    key,
    value: ListFlagReasonCode[key],
    label: ListFlagReasonCode[key]
}));

const FlagReasonContextInputs = (props: FlagReasonContextInputsProps) => {
    const { listEditData, setListEditData } = props;
    // Over-limit values show an error and block Confirm rather than using the native maxlength, which would
    // silently truncate a paste and save the first part of it.
    const errors = getFlagContextErrors(listEditData);

    return (
        <>
            <SelectInput
                id='flag_reason_code'
                placeholder='Select reason (optional)'
                value={listEditData.reasonCode ?? ''}
                onChange={(reasonCode) =>
                    setListEditData((prevState) => ({ ...prevState, reasonCode: reasonCode || undefined }))
                }
                options={REASON_CODE_OPTIONS}
            />
            <Textarea
                id='flag_note'
                value={listEditData.note ?? ''}
                onChange={(note) => setListEditData((prevState) => ({ ...prevState, note: note || undefined }))}
                rows={3}
                resize='none'
                helperText={FLAG_NOTE_HINT}
                error={errors.note !== undefined}
                errorMessage={errors.note}
            />
            <Input
                id='flag_ticket_id'
                type='text'
                placeholder='Ticket ID (optional)'
                value={listEditData.ticketId ?? ''}
                onChange={(ticketId) =>
                    setListEditData((prevState) => ({ ...prevState, ticketId: ticketId || undefined }))
                }
                error={errors.ticketId !== undefined}
                errorMessage={errors.ticketId}
            />
        </>
    );
};

const createInput = (
    list: ListDetails | undefined,
    buttonProps: ListActionButtonConfig<ListButtonId>,
    listEditData: ListEditData,
    setListEditData: Dispatch<SetStateAction<ListEditData>>
): React.ReactNode => {
    switch (buttonProps.id) {
        case 'edit_list_state':
            return (
                <ControlGroup
                    legend='Select state'
                    value={listEditData.state}
                    onChange={(state) => setListEditData((prevState) => ({ ...prevState, state }))}
                >
                    {Object.keys(ListState).map((key) => (
                        <RadioButton key={key} value={ListState[key]}>
                            {ListState[key]}
                        </RadioButton>
                    ))}
                </ControlGroup>
            );
        case 'add_list_flag':
        case 'remove_list_flag':
            return (
                <>
                    <FlagSelectInput
                        list={list}
                        buttonId={buttonProps.id}
                        listEditData={listEditData}
                        setListEditData={setListEditData}
                    />
                    <FlagReasonContextInputs listEditData={listEditData} setListEditData={setListEditData} />
                </>
            );
    }
};

const createModalContent = (
    list: ListDetails | undefined,
    selectedLists: SelectedLists | undefined,
    buttonProps: ListActionButtonConfig<ListButtonId>,
    listEditData: ListEditData,
    setListEditData: Dispatch<SetStateAction<ListEditData>>
): React.ReactNode => (
    <Column spacing='200'>
        {selectedLists && <Heading level={4}>{createSelectedListsMessage(selectedLists)}</Heading>}
        {createInput(list, buttonProps, listEditData, setListEditData)}
        <ModalFooter>
            <Row alignmentHorizontal='end' widths='fit'>
                <Button
                    type='primary'
                    size='small'
                    disabled={hasFlagContextErrors(buttonProps.id, listEditData)}
                    onClick={() => buttonProps.onConfirm(listEditData)}
                >
                    Confirm
                </Button>
            </Row>
        </ModalFooter>
    </Column>
);

const createSelectedListsMessage = (selectedLists: SelectedLists) => {
    const lists = Object.keys(selectedLists).filter((listId) => selectedLists[listId]);
    return lists.length > 1 ? `Editing ${lists.length} lists...` : `Editing ${lists.length} list...`;
};
